// =============================================================================
// OBIEG DOKUMENTÓW — LISTA „DOKUMENTY” (UD-12a, plan 53 §11.2). Logika ekranu,
// BEZ Reacta i bez Supabase, żeby każdą decyzję dało się zmierzyć bez
// renderowania (wzór: `devices/obiegDokumentow.ts`).
//
// Słowa i tony stanów dokumentu NIE mieszkają tutaj — biorą się z kartoteki
// `@insolitum/types` (`obieg.ts`). Progi i granice kontroli biorą się z
// `devices/obiegDokumentow.ts`, tej samej funkcji, z której liczy karta agenta:
// dwie implementacje „kontrola nie nadąża” dałyby na dwóch ekranach dwie różne
// liczby o tym samym dokumencie.
//
// TRZY RZECZY, KTÓRE TEN PLIK PILNUJE:
//
//  * NAJGORSZE NA WIERZCH. Dokument zmieniony albo usunięty w Receptorze po
//    zapisaniu w Universe jest zmieniony także wtedy, gdy z założenia nie idzie
//    do systemu księgowego — stan źródła wygrywa z celem.
//  * ZERO TYLKO PRZY ZMIERZONYM ZERZE. Pusta lista znaczy „nie ma dokumentów”
//    wyłącznie przy ŚWIEŻYM odczycie (kursor aktywny, bez powodu niezlecania,
//    bez porażek, ostatni udany odczyt młodszy niż 15 minut). Harmonogram
//    zleca odczyt tylko w pulsie agenta, więc przy wyłączonym agencie nie
//    rośnie ani licznik porażek, ani powód — wiek odczytu jest wtedy jedynym
//    sygnałem, że pustka nic nie znaczy.
//  * NIEZNANE NIE UDAJE ZNANEGO. Stan źródła albo potwierdzenie spoza CHECK-ów
//    786, kod błędu wystawcy, rodzaj spoza listy — wszystko to daje „Nie
//    podaje” z kodem, nigdy najbliższy znany stan.
// =============================================================================

import {
  CELE_POZA_KSIEGOWOSCIA,
  NAZWY_CELOW,
  NAZWY_RODZAJOW,
  NIE_DOTYCZY,
  NIE_PODAJE,
  POTWIERDZENIA_WYSTAWCY,
  RODZAJE_DOKUMENTU_MAGAZYNOWEGO,
  STANY_ZRODLA,
  type CelDokumentu,
  type KodStanuDokumentu,
  type RodzajDokumentuMagazynowego,
  type TonObiegu,
} from '@insolitum/types';
import { czyLokalizacjaAktywna } from '@insolitum/utils';

import { zLiczba } from '../../../lib/odmien';
import {
  granicePytaniaOZaleglosc,
  type KursorObiegu,
  type PrawaObiegu,
  type ZakladObiegu,
  type ZakladOrganizacji,
} from '../devices/obiegDokumentow';
import { formatujCzasWzgledny } from '../printing/status';

import {
  dzienWStrefie,
  formatujDzien,
  strefaBezpieczna,
  type WynikOkresu,
} from './dzienKalendarzowy';

// ── Zapytanie ────────────────────────────────────────────────────────────────

/**
 * Kolumny nagłówka, o które prosi lista — DOKŁADNIE grant 786 dla
 * `authenticated`, nigdy `*`. Kolumna spoza grantu (np. `kontrahent_nazwa`)
 * kończy CAŁE zapytanie błędem 42501, a atrapa w testach tego nie widzi —
 * zgodność z migracją pilnuje `tests/unit/apps/kontraktListyDokumentow.test.ts`.
 */
export const KOLUMNY_DOKUMENTU_OBIEGU = [
  'id',
  'organization_id',
  'klasa',
  'rodzaj',
  'cel',
  'zaklad_id',
  'wystawca_system',
  'numer_zrodlowy',
  'typ_zrodlowy',
  'seq_zrodlowy',
  'data_operacji',
  'data_wystawienia_at',
  'dzien_produkcji',
  'magazyn_symbol',
  'magazyn_nazwa',
  'magazyn_docelowy_symbol',
  'magazyn_docelowy_nazwa',
  'zlecenie_numer',
  'liczba_pozycji',
  'stan_zrodla',
  'zmiana_w_zrodle_at',
  'wystawca_potwierdzenie',
  'wystawca_potwierdzenie_at',
  'wystawca_sprawdzono_at',
  'wystawca_blad_kod',
  'zarejestrowano_at',
  'kontrakt',
  'podpis_stan',
] as const;

/** Wiersz nagłówka w kształcie, jaki czyta klient (kolumny z grantu 786). */
export interface DokumentObiegu {
  id: string;
  organization_id: string;
  klasa: string;
  rodzaj: string;
  /** NULL dopuszczalny poza klasą magazynową (CHECK `cel_magazynowy`). */
  cel: string | null;
  zaklad_id: string | null;
  wystawca_system: string;
  numer_zrodlowy: string | null;
  typ_zrodlowy: string | null;
  seq_zrodlowy: number | null;
  /** Kolumna `date` — „RRRR-MM-DD”, dzień w strefie zakładu. */
  data_operacji: string;
  data_wystawienia_at: string;
  dzien_produkcji: string | null;
  magazyn_symbol: string | null;
  magazyn_nazwa: string | null;
  magazyn_docelowy_symbol: string | null;
  magazyn_docelowy_nazwa: string | null;
  zlecenie_numer: string | null;
  liczba_pozycji: number;
  stan_zrodla: string;
  zmiana_w_zrodle_at: string | null;
  wystawca_potwierdzenie: string | null;
  wystawca_potwierdzenie_at: string | null;
  wystawca_sprawdzono_at: string | null;
  wystawca_blad_kod: string | null;
  zarejestrowano_at: string;
  kontrakt: string;
  podpis_stan: string;
}

/**
 * Grupa „Wymaga działania” jako filtr PostgREST — STAŁY napis, bez danych
 * użytkownika. To dokładnie kody z tonem `error` w kartotece: stan źródła inny
 * niż `zarejestrowany` (zmieniony, usunięty — i każdy przyszły, spoza CHECK-a,
 * więc nieznane idzie na wierzch) oraz odmowa Receptora.
 */
export const FILTR_WYMAGA_DZIALANIA =
  'stan_zrodla.neq.zarejestrowany,wystawca_potwierdzenie.eq.odmowa';

/** Wierszy na stronę; zapytanie prosi o jeden więcej, żeby wiedzieć, czy jest następna. */
export const ROZMIAR_STRONY = 50;

/**
 * Odczyt jest ŚWIEŻY, gdy ostatni udany był młodszy niż 15 minut (plan 53
 * §13.6.7: najdłuższa przerwa między udanymi odczytami ≤ 15 min; produkcja:
 * mediana odstępu 5:00, maksimum 5:59).
 */
export const PROG_SWIEZOSCI_ODCZYTU_MIN = 15;

/**
 * Potwierdzenie „się opóźnia”, gdy dokument zapisano ponad 30 minut temu,
 * a Receptor go nie potwierdził (plan 53 §13.6.7: ≤ 10 min + 5 min na każde
 * pełne 100 dokumentów zaległości).
 */
export const PROG_OPOZNIENIA_POTWIERDZENIA_MIN = 30;

/**
 * Okno harmonogramu odczytu dokumentów — KOPIA `OKNO_ODCZYTU_MS / 60_000` z
 * `services/receptor-core/src/harmonogramDokumentow.ts` (powłoka nie importuje
 * usług); zgodność pilnuje bramka z korzenia. Od 821 (20.10.2026) 1 min, było 5.
 */
export const OKNO_ODCZYTU_MIN = 1;

/** „w ciągu 1 minuty”, „do 16 minut” — dopełniacz po „w ciągu” i „do”. */
export const minutWDopelniaczu = (n: number): string => zLiczba(n, 'minuty', 'minut', 'minut');

/**
 * Czy stan przekazania czyta intencje wysyłki. W UD-12a NIE — obiektów intencji
 * nie ma w bazie, więc każdy dokument jest „W Universe”. Bramka
 * `kontraktListyDokumentow.test.ts` zapala się na pierwszej migracji, która je
 * zakłada: wtedy `stanPrzekazania` trzeba przepiąć na prawdziwą odpowiedź.
 */
export const PRZEKAZANIE_CZYTA_INTENCJE = false;

/** Cele, przy których dzień produkcji POWINIEN być (NULL = źródło nie podało). */
export const CELE_Z_DNIEM_PRODUKCJI = ['przyjecie_z_produkcji', 'rozchod_produkcyjny'] as const;

// ── Wygląd plakietek ─────────────────────────────────────────────────────────

/**
 * Ton z kartoteki → klasy Aurory (`.pill`). `nieznane` jest NEUTRALNE
 * (token `--ids-tl-nieznane`), a nie bursztynowe, i różni się od szarego
 * „W Universe” kolorem tekstu i glifem „?”.
 */
export const KLASA_TONU: Record<TonObiegu, string> = {
  pending: 'pill pill--pending',
  muted: 'pill pill--muted',
  active: 'pill pill--active',
  trial: 'pill pill--trial',
  error: 'pill pill--error',
  nieznane: 'pill pill--muted text-[color:var(--ids-tl-nieznane)]',
};

// ── Wyprowadzenie stanów dokumentu ───────────────────────────────────────────

const MINUTA = 60_000;

/**
 * Jedna plakietka kolumny „Przekazanie” — najgorsza wygrywa:
 * usunięty → zmieniony → stan źródła spoza CHECK-a → cel poza księgowością →
 * „W Universe”.
 */
export function stanPrzekazania(d: Pick<DokumentObiegu, 'stan_zrodla' | 'cel'>): KodStanuDokumentu {
  if (d.stan_zrodla === 'usuniety_w_zrodle') return 'usuniety_w_zrodle';
  if (d.stan_zrodla === 'zmieniony_w_zrodle') return 'zmieniony_w_zrodle';
  if (!(STANY_ZRODLA as readonly string[]).includes(d.stan_zrodla)) return 'nie_podaje';
  if (d.cel !== null && (CELE_POZA_KSIEGOWOSCIA as readonly string[]).includes(d.cel)) {
    return 'nie_dotyczy';
  }
  return 'bez_odbiorcy';
}

export interface WynikPotwierdzenia {
  kod: KodStanuDokumentu;
  /** Kod błędu wystawcy albo nieznana wartość potwierdzenia — pokazywane dosłownie. */
  surowy: string | null;
}

/**
 * Plakietka potwierdzenia w kolumnie „Receptor”. Niezerowy `wystawca_blad_kod`
 * daje „Nie podaje” z kodem: gałąź `blad` w funkcji odpowiedzi wystawcy nie ma
 * dziś producenta, a ekran nie zgaduje, co kod znaczy.
 */
export function stanPotwierdzenia(
  d: Pick<DokumentObiegu, 'wystawca_blad_kod' | 'wystawca_potwierdzenie' | 'zarejestrowano_at'>,
  teraz: number
): WynikPotwierdzenia {
  if (d.wystawca_blad_kod !== null && d.wystawca_blad_kod !== '') {
    return { kod: 'nie_podaje', surowy: d.wystawca_blad_kod };
  }
  const p = d.wystawca_potwierdzenie;
  if (p !== null && !(POTWIERDZENIA_WYSTAWCY as readonly string[]).includes(p)) {
    return { kod: 'nie_podaje', surowy: p };
  }
  if (p === 'odmowa') return { kod: 'odmowa', surowy: null };
  if (p === 'potwierdzony') return { kod: 'potwierdzony', surowy: null };
  const zapisano = Date.parse(d.zarejestrowano_at);
  if (!Number.isFinite(zapisano)) return { kod: 'nie_podaje', surowy: null };
  return teraz - zapisano > PROG_OPOZNIENIA_POTWIERDZENIA_MIN * MINUTA
    ? { kod: 'potwierdzenie_opoznione', surowy: null }
    : { kod: 'czeka_na_potwierdzenie', surowy: null };
}

export interface WynikKontroli {
  kod: KodStanuDokumentu;
  sprawdzonoAt: string | null;
}

/**
 * Druga linia komórki „Receptor” — kontrola potwierdzonego dokumentu.
 * `null` = kontrola tego dokumentu nie dotyczy (niepotwierdzony, zmieniony
 * u źródła) albo jest wstrzymana po błędzie wystawcy, więc ekran nie zgaduje.
 * Granice liczy TA SAMA funkcja co zliczenie karty agenta, a porównanie z
 * `sprawdzoneDo` jest OSTRE, tak jak `.lt(...)` w tamtym zapytaniu.
 */
export function stanKontroli(
  d: Pick<
    DokumentObiegu,
    | 'stan_zrodla'
    | 'wystawca_potwierdzenie'
    | 'wystawca_potwierdzenie_at'
    | 'wystawca_sprawdzono_at'
    | 'wystawca_blad_kod'
  >,
  teraz: number
): WynikKontroli | null {
  if (d.wystawca_potwierdzenie !== 'potwierdzony' || d.stan_zrodla !== 'zarejestrowany') {
    return null;
  }
  if (d.wystawca_blad_kod !== null && d.wystawca_blad_kod !== '') return null;
  const { potwierdzoneOd, sprawdzoneDo } = granicePytaniaOZaleglosc(teraz);
  const potwierdzono = d.wystawca_potwierdzenie_at ? Date.parse(d.wystawca_potwierdzenie_at) : NaN;
  if (!Number.isFinite(potwierdzono)) return { kod: 'nie_podaje', sprawdzonoAt: null };
  if (potwierdzono < Date.parse(potwierdzoneOd)) {
    return { kod: 'kontrola_poza_oknem', sprawdzonoAt: d.wystawca_sprawdzono_at };
  }
  // Potwierdzenie ustawia `wystawca_sprawdzono_at = now()` w tej samej
  // instrukcji, więc NULL tu nie powstaje. Karta takiego dokumentu nie liczy,
  // więc i lista nie nazwie go zaległym.
  const sprawdzono = d.wystawca_sprawdzono_at ? Date.parse(d.wystawca_sprawdzono_at) : NaN;
  if (!Number.isFinite(sprawdzono)) return { kod: 'nie_podaje', sprawdzonoAt: null };
  if (sprawdzono < Date.parse(sprawdzoneDo)) {
    return { kod: 'kontrola_nie_nadaza', sprawdzonoAt: d.wystawca_sprawdzono_at };
  }
  return { kod: 'kontrola_biezaca', sprawdzonoAt: d.wystawca_sprawdzono_at };
}

/**
 * Dzień produkcji: data, „nie dotyczy” albo „nie podaje”. Reguła z celu:
 * przyjęcie z produkcji i zużycie w produkcji MAJĄ dzień produkcji (z
 * zlecenia), więc NULL tam znaczy „źródło nie podało”; przy pozostałych celach
 * NULL znaczy, że pole nie ma zastosowania.
 */
export function dzienProdukcji(
  d: Pick<DokumentObiegu, 'dzien_produkcji' | 'cel'>
): { wartosc: string } | 'nie_dotyczy' | 'nie_podaje' {
  if (d.dzien_produkcji) return { wartosc: d.dzien_produkcji };
  return d.cel !== null && (CELE_Z_DNIEM_PRODUKCJI as readonly string[]).includes(d.cel)
    ? 'nie_podaje'
    : 'nie_dotyczy';
}

/** „PW · przyjęcie wewnętrzne (wyrób z produkcji)” albo „Nie podaje · {kod}”. */
export function opisRodzaju(d: Pick<DokumentObiegu, 'klasa' | 'rodzaj'>): {
  tekst: string;
  znany: boolean;
} {
  if (d.klasa === 'magazynowy' && Object.prototype.hasOwnProperty.call(NAZWY_RODZAJOW, d.rodzaj)) {
    const r = NAZWY_RODZAJOW[d.rodzaj as RodzajDokumentuMagazynowego];
    return { tekst: `${r.skrot} · ${r.nazwa}`, znany: true };
  }
  const kod = d.klasa === 'magazynowy' ? d.rodzaj : `${d.klasa}/${d.rodzaj}`;
  return { tekst: `Nie podaje · ${kod}`, znany: false };
}

/** Etykieta celu; NULL → „nie dotyczy”, kod spoza słownika → „Nie podaje · {kod}”. */
export function opisCelu(cel: string | null): string {
  if (cel === null) return NIE_DOTYCZY;
  return Object.prototype.hasOwnProperty.call(NAZWY_CELOW, cel)
    ? NAZWY_CELOW[cel as CelDokumentu]
    : `Nie podaje · ${cel}`;
}

const symbolINazwa = (symbol: string | null, nazwa: string | null): string | null => {
  const czesci = [symbol, nazwa].filter(
    (s): s is string => typeof s === 'string' && s.trim() !== ''
  );
  return czesci.length > 0 ? czesci.join(' · ') : null;
};

/** „MS · Magazyn surowców”, przy MM „źródłowy → docelowy”; brak obu → „nie podaje”. */
export function opisMagazynu(
  d: Pick<
    DokumentObiegu,
    | 'rodzaj'
    | 'magazyn_symbol'
    | 'magazyn_nazwa'
    | 'magazyn_docelowy_symbol'
    | 'magazyn_docelowy_nazwa'
  >
): string {
  const zrodlowy = symbolINazwa(d.magazyn_symbol, d.magazyn_nazwa);
  if (d.rodzaj !== 'mm') return zrodlowy ?? NIE_PODAJE;
  const docelowy = symbolINazwa(d.magazyn_docelowy_symbol, d.magazyn_docelowy_nazwa);
  if (zrodlowy === null && docelowy === null) return NIE_PODAJE;
  return `${zrodlowy ?? NIE_PODAJE} → ${docelowy ?? NIE_PODAJE}`;
}

// ── Chwile i strefy ──────────────────────────────────────────────────────────

/**
 * „16.10.2026, 02:21” w strefie zakładu (NULL / nieznana → strefa
 * przeglądarki, bez `RangeError`). Napis, który chwilą nie jest, wraca bez zmian.
 */
export function formatujChwile(iso: string, strefa: string | null | undefined): string {
  const chwila = Date.parse(iso);
  if (!Number.isFinite(chwila)) return iso;
  return new Intl.DateTimeFormat('pl-PL', {
    timeZone: strefaBezpieczna(strefa),
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(chwila);
}

/** Strefa zakładu z listy zakładów; lista nieznana albo zakład spoza niej → `null`. */
export function strefaZakladu(
  zaklady: readonly ZakladObiegu[] | 'blad' | null | undefined,
  zakladId: string | null
): string | null {
  if (!Array.isArray(zaklady) || zakladId === null) return null;
  return zaklady.find((z) => z.id === zakladId)?.timezone ?? null;
}

/**
 * Strefa, w której liczy się „dziś” okresu: strefa zakładu z filtra, a bez
 * filtra — strefa jedynego AKTYWNEGO zakładu (P2: zakład usunięty na ekranie
 * Lokalizacje zostaje `inactive` i nie odbiera organizacji „jedynego zakładu” —
 * ta sama reguła co strefa pieca Furnusa); inaczej strefa przeglądarki (`null`).
 */
export function strefaOkresu(
  zaklady: readonly ZakladOrganizacji[] | 'blad' | null,
  zakladId: string | null = null
): string | null {
  if (!Array.isArray(zaklady)) return null;
  const wybrany = zakladId === null ? undefined : zaklady.find((z) => z.id === zakladId);
  if (wybrany) return wybrany.timezone;
  const aktywne = zaklady.filter((z) => czyLokalizacjaAktywna(z.status));
  return aktywne.length === 1 ? aktywne[0].timezone : null;
}

/** Dzień `od` kursora w strefie jego zakładu („RRRR-MM-DD”). */
export function dzienOdKursora(
  k: KursorObiegu,
  zaklady: readonly ZakladObiegu[] | 'blad' | null
): string {
  return dzienWStrefie(k.od, strefaZakladu(zaklady, k.zaklad_id));
}

/** Najwcześniejszy dzień `od` spośród kursorów albo `null` bez kursorów. */
export function najwczesniejszyDzienOd(
  kursory: readonly KursorObiegu[],
  zaklady: readonly ZakladObiegu[] | 'blad' | null
): string | null {
  const dni = kursory.map((k) => dzienOdKursora(k, zaklady)).sort();
  return dni[0] ?? null;
}

// ── Kursor: świeżość, pominięte, zdania ──────────────────────────────────────

/** Wiek ostatniego udanego odczytu w ms albo `null` (odczytu jeszcze nie było). */
export function wiekOdczytu(k: KursorObiegu, teraz: number): number | null {
  if (!k.ostatni_odczyt_at) return null;
  const chwila = Date.parse(k.ostatni_odczyt_at);
  return Number.isFinite(chwila) ? teraz - chwila : null;
}

/** Czy odczyt jest ŚWIEŻY — jedyny stan, w którym pusta lista znaczy „zero”. */
export function czySwiezyOdczyt(k: KursorObiegu, teraz: number): boolean {
  const wiek = wiekOdczytu(k, teraz);
  return (
    k.stan === 'aktywny' &&
    (k.harmonogram_powod === null || k.harmonogram_powod === '') &&
    k.porazki_z_rzedu === 0 &&
    wiek !== null &&
    wiek < PROG_SWIEZOSCI_ODCZYTU_MIN * MINUTA
  );
}

/**
 * Dokumenty pominięte przy rejestracji (firma nieustalona albo inna firma w tej
 * samej bazie Receptora) — narastająco od włączenia odczytu.
 */
export function pominieteDokumenty(k: KursorObiegu): number {
  const l = k.liczniki;
  if (typeof l !== 'object' || l === null || Array.isArray(l)) return 0;
  const liczba = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 0);
  return liczba(l.firma_nieustalona) + liczba(l.poza_firma);
}

/** „Universe czyta dokumenty założone w Receptorze od …”. */
export function zdanieOd(
  k: KursorObiegu,
  zaklady: readonly ZakladObiegu[] | 'blad' | null
): string {
  return `Universe czyta dokumenty założone w Receptorze od ${formatujChwile(
    k.od,
    strefaZakladu(zaklady, k.zaklad_id)
  )}. Wcześniejszych tu nie ma.`;
}

export function zdaniePominietych(n: number): string {
  return `Od włączenia odczytu Universe pominął ${n} dok. z Receptora, bo nie dało się ustalić ich firmy albo należą do innej firmy w tej bazie Receptora. Tych dokumentów nie ma na liście — zgłoś to opiekunowi.`;
}

export function zdanieNieswiezegoOdczytu(k: KursorObiegu, teraz: number): string {
  return `Ostatni udany odczyt był ${formatujCzasWzgledny(
    k.ostatni_odczyt_at,
    teraz
  )} — lista może nie mieć najnowszych dokumentów. Sprawdź, czy agent przy serwerze z Receptorem działa.`;
}

export function zdanieOpoznienia(k: KursorObiegu): string {
  return `Nowy dokument pojawia się tu zwykle do ${minutWDopelniaczu(
    k.karencja_min + OKNO_ODCZYTU_MIN
  )} po zatwierdzeniu w Receptorze.`;
}

/** Zdanie stanu 16 — tylko przy świeżym odczycie, zerze pominiętych i okresie po `od`. */
export const ZDANIE_POPRAWNY_WYNIK =
  'Dzień bez zatwierdzonego dokumentu w Receptorze to poprawny wynik.';

// ── Najnowszy dokument w rejestrze (stany 15–16) ─────────────────────────────

/**
 * Stany, w których ekran pyta o najnowszy dokument organizacji: zero przy
 * ŚWIEŻYM odczycie, po filtrach albo w samym okresie. Rozdziela „pusto w tym
 * okresie” od „pusto w ogóle” — w pozostałych stanach pustka znaczy co innego
 * (odczyt stoi, nie ruszył, jest niepewny), więc to pytanie nic by nie dodało.
 */
export const STANY_Z_NAJNOWSZYM = ['zero_po_filtrach', 'zero_w_okresie'] as const;

/** Najnowszy dokument organizacji — trzy kolumny z grantu 786. */
export interface NajnowszyDokument {
  numer_zrodlowy: string | null;
  rodzaj: string;
  data_operacji: string;
}

/**
 * Zdanie o najnowszym dokumencie: trzy stany, bo każdy mówi co innego —
 * dokument jest, rejestr jest pusty, nie udało się sprawdzić (to NIE jest
 * „pusty”). `undefined` = pytanie w toku, więc zdania jeszcze nie ma.
 */
export function zdanieNajnowszegoDokumentu(
  n: NajnowszyDokument | null | 'blad' | undefined
): string | null {
  if (n === undefined) return null;
  if (n === 'blad') return 'Nie udało się sprawdzić, który dokument jest w rejestrze najnowszy.';
  if (n === null) return 'W rejestrze nie ma jeszcze żadnego dokumentu.';
  const numer = n.numer_zrodlowy?.trim() || 'bez numeru';
  const rodzaj = Object.prototype.hasOwnProperty.call(NAZWY_RODZAJOW, n.rodzaj)
    ? NAZWY_RODZAJOW[n.rodzaj as RodzajDokumentuMagazynowego].skrot
    : `Nie podaje · ${n.rodzaj}`;
  return `Najnowszy dokument w rejestrze: ${numer} (${rodzaj}), data operacji ${formatujDzien(
    n.data_operacji
  )}.`;
}

// ── Filtry w adresie ─────────────────────────────────────────────────────────

/** Najdłuższy numer, którego szukamy — dłuższy w adresie to filtr zdjęty. */
export const MAKS_DLUGOSC_NUMERU = 64;

export interface FiltryListy {
  od: string;
  doDnia: string;
  wymagaDzialania: boolean;
  strona: number;
  /** Rodzaj z listy CHECK-a 786; wartość spoza niej → `null`. */
  rodzaj: RodzajDokumentuMagazynowego | null;
  /** UUID zakładu; napis, który UUID-em nie jest → `null`. */
  zakladId: string | null;
  /** Fragment numeru u wystawcy, przycięty, najwyżej 64 znaki; inaczej `null`. */
  nr: string | null;
}

const WZOR_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Czy napis to UUID — tylko taki idzie do `eq('zaklad_id', …)`. */
export function czyUuid(s: string | null | undefined): boolean {
  return typeof s === 'string' && WZOR_UUID.test(s);
}

/**
 * Fragment numeru do wzorca `ilike`: `\`, `%` i `_` poprzedzone `\` (domyślny
 * znak ucieczki LIKE), żeby „PW_1” szukało podkreślnika, a nie dowolnego znaku.
 */
export function ucieknijLike(s: string): string {
  return s.replace(/[\\%_]/g, (znak) => `\\${znak}`);
}

/** Zakład z adresu — osobno, bo od niego zależy strefa okresu domyślnego. */
export function czytajZaklad(sp: URLSearchParams): string | null {
  const zaklad = sp.get('zaklad');
  return czyUuid(zaklad) ? zaklad : null;
}

/**
 * Filtry z adresu. Brak parametru daje okres domyślny; zła wartość grupy,
 * strony, rodzaju, zakładu albo numeru daje filtr zdjęty, nigdy wyjątek. Daty
 * NIE są tu poprawiane — zła data idzie do `walidujOkres`, a ekran mówi, co
 * poprawić.
 */
export function czytajFiltry(
  sp: URLSearchParams,
  domyslny: { od: string; doDnia: string }
): FiltryListy {
  const strona = sp.get('strona') ?? '';
  const rodzaj = sp.get('rodzaj') ?? '';
  const nr = (sp.get('nr') ?? '').trim();
  return {
    od: sp.get('od') ?? domyslny.od,
    doDnia: sp.get('do') ?? domyslny.doDnia,
    wymagaDzialania: sp.get('grupa') === 'wymaga_dzialania',
    strona: /^[1-9]\d{0,5}$/.test(strona) ? Number(strona) : 1,
    rodzaj: (RODZAJE_DOKUMENTU_MAGAZYNOWEGO as readonly string[]).includes(rodzaj)
      ? (rodzaj as RodzajDokumentuMagazynowego)
      : null,
    zakladId: czytajZaklad(sp),
    nr: nr !== '' && nr.length <= MAKS_DLUGOSC_NUMERU ? nr : null,
  };
}

/** Czy ustawiony jest filtr rodzaju, zakładu albo numeru (stan 15 zamiast 16). */
export function saFiltrySzczegolowe(f: Pick<FiltryListy, 'rodzaj' | 'zakladId' | 'nr'>): boolean {
  return f.rodzaj !== null || f.zakladId !== null || f.nr !== null;
}

/**
 * Filtr zakładu, który naprawdę obowiązuje. Pole jest widoczne dopiero przy
 * dwóch zakładach, więc przy jednym (albo przy zakładzie spoza listy) filtr
 * z adresu nie działa — ekran nie filtruje po czymś, czego człowiek nie widzi.
 * Lista jeszcze niewczytana (albo nieudana) nie rozstrzyga: zostaje adres.
 */
export function zakladFiltra(
  zakladId: string | null,
  zaklady: readonly ZakladObiegu[] | 'blad' | null
): string | null {
  if (zakladId === null || !Array.isArray(zaklady)) return zakladId;
  return zaklady.length >= 2 && zaklady.some((z) => z.id === zakladId) ? zakladId : null;
}

// ── Stan ekranu ──────────────────────────────────────────────────────────────

/** Stany listy w kolejności rozstrzygania — pierwszy pasujący wygrywa (§3.9). */
export const STANY_LISTY = [
  'bez_organizacji',
  'brak_prawa',
  'okres_niepoprawny',
  'ladowanie',
  'blad_odczytu',
  'lista',
  'pusto_stan_nieznany',
  'nie_ma_skad',
  'przed_startem',
  'zatrzymany',
  'nie_zlecany',
  'nie_czytano',
  'odczyt_niepewny',
  'nic_do_dzialania',
  'zero_po_filtrach',
  'zero_w_okresie',
] as const;

export type StanListy = (typeof STANY_LISTY)[number];
export type StanZTytulem = Exclude<StanListy, 'ladowanie' | 'lista'>;

/**
 * Tytuł każdego stanu bez tabeli — PARAMI RÓŻNE. Dwa stany pod tym samym
 * tytułem to jedno zdanie o dwóch różnych rzeczach, z których jedna jest
 * nieprawdą.
 */
export const TYTULY_STANOW: Record<StanZTytulem, string> = {
  brak_prawa: 'Dokumenty z Receptora widzi właściciel, administrator albo kierownik firmy.',
  bez_organizacji: 'Wybierz firmę, aby zobaczyć jej dokumenty.',
  okres_niepoprawny: 'Popraw okres, żeby zobaczyć dokumenty.',
  blad_odczytu: 'Nie udało się wczytać dokumentów.',
  pusto_stan_nieznany: 'Nie wiemy, czy w wybranym okresie są dokumenty.',
  nie_ma_skad: 'Nie ma skąd brać dokumentów.',
  przed_startem: 'Ten okres jest sprzed włączenia odczytu.',
  zatrzymany: 'Odczyt dokumentów jest zatrzymany.',
  nie_zlecany: 'Odczyt nie jest teraz zlecany.',
  nie_czytano: 'Jeszcze nie czytaliśmy dokumentów z Receptora.',
  odczyt_niepewny: 'Nie wiemy, czy są nowe dokumenty.',
  nic_do_dzialania: 'Nic nie wymaga działania w wybranym okresie.',
  zero_po_filtrach: 'Żaden dokument nie pasuje do filtrów.',
  zero_w_okresie: 'W wybranym okresie nie ma dokumentów.',
};

export interface WejscieStanuListy {
  prawa: PrawaObiegu;
  /** Czy jest bieżąca organizacja. */
  organizacja: boolean;
  okres: WynikOkresu;
  /** `null` = wczytywanie, `'blad'` = odczyt się nie udał. */
  lista: readonly DokumentObiegu[] | 'blad' | null;
  kursory: readonly KursorObiegu[] | 'blad' | null;
  zaklady: readonly ZakladObiegu[] | 'blad' | null;
  /** Chip „Wymaga działania”. */
  wymagaDzialania: boolean;
  /** Ustawiony filtr rodzaju, zakładu albo numeru (`saFiltrySzczegolowe`). */
  filtrySzczegolowe: boolean;
  /**
   * Bieżąca strona. Pusta strona 2+ nie mówi nic o okresie (adres z dawnym
   * numerem strony) — ekran wraca wtedy na stronę 1, a do tego czasu to ładowanie.
   */
  strona: number;
  teraz: number;
}

export interface WynikStanuListy {
  stan: StanListy;
  /** Kursor, o którym mówi stan (zatrzymany, niezlecany, niepewny…), albo `null`. */
  kursor: KursorObiegu | null;
}

/**
 * Stan ekranu listy — §3.9 planu, pierwszy pasujący wygrywa. Kursorów jest do
 * ARCH PR 1.7 najwyżej jeden; przy kilku rozstrzyga NAJGORSZY, bo „zero”
 * wolno powiedzieć tylko wtedy, gdy każdy odczyt jest świeży.
 */
export function stanListy(w: WejscieStanuListy): WynikStanuListy {
  const bez = (stan: StanListy): WynikStanuListy => ({ stan, kursor: null });
  // Bez organizacji nie ma roli, więc „brak prawa” byłby zdaniem o czymś innym
  // niż prawda („wybierz firmę”) — organizacja rozstrzyga się pierwsza.
  if (!w.organizacja) return bez('bez_organizacji');
  if (!w.prawa.podglad) return bez('brak_prawa');
  if (!w.okres.ok) return bez('okres_niepoprawny');
  if (w.lista === null) return bez('ladowanie');
  if (w.lista === 'blad') return bez('blad_odczytu');
  if (w.lista.length > 0) return bez('lista');
  if (w.strona > 1) return bez('ladowanie');

  // Lista pusta: co ta pustka znaczy, mówi dopiero kursor.
  if (w.kursory === null) return bez('ladowanie');
  if (w.kursory === 'blad') return bez('pusto_stan_nieznany');
  if (w.kursory.length === 0) return bez('nie_ma_skad');

  const dzienOd = najwczesniejszyDzienOd(w.kursory, w.zaklady);
  if (dzienOd !== null && w.okres.doDnia < dzienOd) {
    return {
      stan: 'przed_startem',
      kursor: w.kursory.find((k) => dzienOdKursora(k, w.zaklady) === dzienOd) ?? null,
    };
  }

  const aktywne = w.kursory.filter((k) => k.stan === 'aktywny');
  if (aktywne.length === 0) return { stan: 'zatrzymany', kursor: w.kursory[0] };

  const niezlecany = aktywne.find(
    (k) => k.harmonogram_powod !== null && k.harmonogram_powod !== ''
  );
  if (niezlecany) return { stan: 'nie_zlecany', kursor: niezlecany };

  const bezOdczytu = aktywne.find((k) => !k.ostatni_odczyt_at);
  if (bezOdczytu) return { stan: 'nie_czytano', kursor: bezOdczytu };

  const nieswiezy = w.kursory.find((k) => !czySwiezyOdczyt(k, w.teraz));
  if (nieswiezy) return { stan: 'odczyt_niepewny', kursor: nieswiezy };

  const kursor = w.kursory[0];
  if (w.filtrySzczegolowe) return { stan: 'zero_po_filtrach', kursor };
  if (w.wymagaDzialania) return { stan: 'nic_do_dzialania', kursor };
  return { stan: 'zero_w_okresie', kursor };
}

/** Czy okres listy zaczyna się przed dniem `od` któregoś kursora. */
export function okresSiegaPrzedOd(
  okres: WynikOkresu,
  kursory: readonly KursorObiegu[] | 'blad' | null,
  zaklady: readonly ZakladObiegu[] | 'blad' | null
): KursorObiegu | null {
  if (!okres.ok || !Array.isArray(kursory)) return null;
  return kursory.find((k) => okres.od < dzienOdKursora(k, zaklady)) ?? null;
}
